import React, { useState } from 'react';
import Avatar from './Avatar';
import { openProfile } from '../lib/nav';
import { api } from '../lib/api';
import SafetyMenu from './SafetyMenu';

// Kept in step with DECLINE_REASONS in api/app/matching/learning.py — each one
// teaches the matcher something different ("busy" pauses, "already know them"
// never re-introduces, "wrong stage" tightens stage).
const DECLINE_REASONS = [
  ['not_relevant', 'Not relevant to me'],
  ['wrong_stage', 'Wrong stage'],
  ['know_them', 'Already know them'],
  ['busy', 'Busy right now'],
];

/* The one screen where the product's argument is visible: you did not ask for
   this, they did not ask for this, and neither of you will ever learn what the
   other said. */
export default function ProposalCard({ proposal, notify, onDecided }) {
  const [busy, setBusy] = useState(false);
  const [why, setWhy] = useState(false);
  const [asking, setAsking] = useState(false);
  // A member asked you directly (People → Discover), rather than B/N suggesting it.
  const asked = proposal.basis === 'ask';

  async function decide(decision, feedback = '') {
    setBusy(true);
    try {
      const r = await api.decideProposal(proposal.id, decision, feedback);
      if (decision === 'accept') {
        notify(
          r.connected
            ? `Connected with ${proposal.other.name} — you can message each other now`
            : 'Noted. If they say yes too, you will be connected.'
        );
      } else {
        notify('Noted. They will not be told.');
      }
      onDecided?.();
    } catch (e) {
      notify(e.message);
      setBusy(false);
    }
  }

  return (
    <article className="card proposal">
      <div className={`prop-flag${asked ? ' asked' : ''}`}>{asked ? 'Asked to connect' : 'Introduction from B/N'}</div>
      <div className="prop-body">
        <div className="prop-who">
          <Avatar person={proposal.other} className="prop-av" />
          <div>
            <button type="button" className="person-link name" onClick={() => openProfile(proposal.other.handle)}><b>{proposal.other.name}</b></button>
            <span className="muted-sm">{proposal.other.headline}</span>
          </div>
          <SafetyMenu person={proposal.other} context="proposal" contextId={proposal.id} notify={notify}
            onDone={() => onDecided?.()} />
        </div>

        <p className="prop-reason">{proposal.reason}</p>
        {proposal.opener && !asked && <p className="prop-opener">{proposal.opener}</p>}

        <div className="prop-actions">
          <button className="btn apply" disabled={busy} onClick={() => decide('accept')}>
            {busy ? '…' : asked ? 'ACCEPT' : 'CONNECT'} <span className="arw">›</span>
          </button>
          <button className="btn quiet" disabled={busy} onClick={() => (asked ? decide('decline') : setAsking(!asking))}>
            {asked ? 'Decline' : 'Not now'}
          </button>
          {!asked && <button className="linklike" onClick={() => setWhy(!why)}>why am I seeing this?</button>}
        </div>
        <p className="prop-what">
          Connecting lets you two message and tag each other. It's not following: everyone's posts
          show on Home either way.
        </p>

        {asking && (
          <div className="prop-reasons">
            <span className="muted-sm">Why not? It helps us introduce you better. They won't be told.</span>
            {DECLINE_REASONS.map(([key, label]) => (
              <button key={key} className="btn quiet sm" disabled={busy} onClick={() => decide('decline', key)}>
                {label}
              </button>
            ))}
          </div>
        )}

        {why && (
          <p className="prop-explain">
            {proposal.basis === 'evidence'
              ? 'This was triggered by something that actually happened, not a guess — you can check it yourself.'
              : 'This was inferred from what you both declared. No interaction between you yet.'}{' '}
            Both of you were asked at the same time. Neither of you will be told what the other
            said, and nothing opens unless you both say yes.
          </p>
        )}
      </div>
    </article>
  );
}
